import {
  parseEvent,
  type CompanionBridgeEvent,
  type CompanionMate,
  type PersonaKey,
} from "@m8t-stack/companion-bridge-contract";
import { stat } from "node:fs/promises";
import { getFoundryToken } from "./auth.js";
import { getConfigPath } from "./config.js";
import {
  resolveGatewayContext,
  type GatewayContext,
} from "./gateway-context.js";

const RESPONSE_MAX_BYTES = 1_000_000;
const REQUEST_DEADLINE_MS = 120_000;
const OPAQUE_ID = /^[A-Za-z0-9_-]{1,256}$/u;
const AGENT_NAME_MAX_LENGTH = 256;

export interface CompanionChatDeps {
  readonly fetch?: typeof fetch;
  readonly resolveGatewayContext?: typeof resolveGatewayContext;
  readonly getFoundryToken?: typeof getFoundryToken;
  readonly now?: () => number;
  readonly deadlineMs?: number;
  readonly installEpoch?: () => Promise<number | null>;
}

export interface CompanionSend {
  readonly requestId: string;
  readonly personaKey: PersonaKey;
  readonly draftRevision: number;
  readonly text: string;
}

type EventSink = (event: CompanionBridgeEvent) => void;
type FailureReason = Extract<
  CompanionBridgeEvent,
  { type: "failed" }
>["reason"];

class PredispatchFailure extends Error {
  constructor(readonly reason: FailureReason) {
    super("companion bridge pre-dispatch failure");
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function isAgentName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= AGENT_NAME_MAX_LENGTH &&
    !hasControlCharacter(value)
  );
}

function authenticatedHeaders(token: string): Record<string, string> {
  return {
    accept: "application/json",
    authorization: `Bearer ${token}`,
  };
}

function gatewayOrigin(gateway: GatewayContext): string {
  let url: URL;
  try {
    url = new URL(gateway.gatewayUrl);
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    throw new PredispatchFailure("not-connected");
  }
  return url.origin;
}

async function readBoundedText(
  response: Response,
  maxBytes = RESPONSE_MAX_BYTES,
): Promise<string> {
  const declared = response.headers.get("content-length");
  if (declared !== null && Number(declared) > maxBytes) {
    throw new Error("bounded response rejected");
  }
  if (!response.body) throw new Error("response body unavailable");

  const reader = (
    response.body as ReadableStream<Uint8Array>
  ).getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let total = 0;
  let text = "";
  for (;;) {
    const part = await reader.read();
    if (part.done) break;
    total += part.value.byteLength;
    if (total > maxBytes) {
      await reader.cancel().catch(() => undefined);
      throw new Error("bounded response rejected");
    }
    text += decoder.decode(part.value, { stream: true });
  }
  text += decoder.decode();
  return text;
}

async function readJsonEnvelope(response: Response): Promise<unknown> {
  if (!response.ok) {
    const reason =
      response.status === 401 || response.status === 403
        ? "not-authorized"
        : response.status === 409
          ? "busy"
          : "not-connected";
    throw new PredispatchFailure(reason);
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new PredispatchFailure("not-connected");
  }

  let envelope: unknown;
  try {
    envelope = JSON.parse(await readBoundedText(response));
  } catch (error) {
    if (error instanceof PredispatchFailure) throw error;
    throw new PredispatchFailure("not-connected");
  }
  if (!isRecord(envelope) || envelope.ok !== true || !("data" in envelope)) {
    throw new PredispatchFailure("not-connected");
  }
  return envelope.data;
}

function decodeAgents(data: unknown): readonly Record<string, unknown>[] {
  if (!isRecord(data) || !Array.isArray(data.agents)) {
    throw new PredispatchFailure("mate-unavailable");
  }
  return data.agents.filter(isRecord);
}

function resolveAgentName(
  agents: readonly Record<string, unknown>[],
  personaKey: PersonaKey,
): string {
  const matches = agents.filter(
    (agent) =>
      agent.personaKey === personaKey && isAgentName(agent.name),
  );
  if (matches.length !== 1) {
    throw new PredispatchFailure("mate-unavailable");
  }
  const match = matches[0];
  if (!isAgentName(match.name)) {
    throw new PredispatchFailure("mate-unavailable");
  }
  return match.name;
}

function decodeConversationId(data: unknown): string {
  if (
    !isRecord(data) ||
    typeof data.id !== "string" ||
    !OPAQUE_ID.test(data.id)
  ) {
    throw new PredispatchFailure("not-connected");
  }
  return data.id;
}

function decodeLastMessageId(data: unknown): string | undefined {
  if (!isRecord(data) || !Array.isArray(data.messages)) {
    throw new PredispatchFailure("not-connected");
  }
  const messages = data.messages as readonly unknown[];
  const last: unknown = messages.at(-1);
  if (
    !isRecord(last) ||
    typeof last.id !== "string" ||
    !OPAQUE_ID.test(last.id)
  ) {
    return undefined;
  }
  return last.id;
}

function mateRoute(
  personaKey: PersonaKey,
  desktopAfter?: string,
): string {
  const marker =
    desktopAfter === undefined ? "" : `&desktopAfter=${desktopAfter}`;
  return `/mates/${personaKey}?desktopPending=1${marker}`;
}

function emitFailure(
  sink: EventSink,
  reason: FailureReason,
  requestId?: string,
): CompanionBridgeEvent {
  const event = parseEvent(
    requestId === undefined
      ? { type: "failed", reason }
      : { type: "failed", requestId, reason },
  );
  sink(event);
  return event;
}

function classifyUnknownPredispatch(error: unknown): FailureReason {
  return error instanceof PredispatchFailure
    ? error.reason
    : "not-connected";
}

/**
 * The gateway accepts both its own API audience and the Foundry audience, but
 * an interactive caller's bearer is forwarded to Foundry unchanged. A token
 * minted for `api://<gatewayClientId>` therefore reaches Foundry with the wrong
 * audience and is rejected, so every companion request carries the Foundry
 * audience — the same one the web product's sign-in asks for.
 */
async function acquireContext(
  deps: CompanionChatDeps,
): Promise<{ token: string; origin: string }> {
  let gateway: GatewayContext;
  let token: string;
  try {
    gateway = await (deps.resolveGatewayContext ?? resolveGatewayContext)({
      interactive: false,
    });
    token = await (deps.getFoundryToken ?? getFoundryToken)();
  } catch {
    throw new PredispatchFailure("not-authorized");
  }
  return { token, origin: gatewayOrigin(gateway) };
}

async function fetchAgents(
  fetchImpl: typeof fetch,
  origin: string,
  token: string,
  signal: AbortSignal,
): Promise<readonly Record<string, unknown>[]> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/api/agents`, {
      headers: authenticatedHeaders(token),
      signal,
    });
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return decodeAgents(await readJsonEnvelope(response));
}

function createDeadline(
  deps: CompanionChatDeps,
): { controller: AbortController; timer: ReturnType<typeof setTimeout> } {
  const controller = new AbortController();
  const timer = setTimeout(
    () => {
      controller.abort();
    },
    deps.deadlineMs ?? REQUEST_DEADLINE_MS,
  );
  return { controller, timer };
}

/**
 * When m8t was installed on this workstation, taken from the earliest
 * timestamp on the CLI configuration file. The mates have been reachable since
 * the platform installation that created it, so this is the honest lower bound
 * for "Active for …". Returns null when the file cannot be read.
 */
async function readInstallEpoch(): Promise<number | null> {
  try {
    const stats = await stat(getConfigPath());
    const candidates = [stats.birthtimeMs, stats.mtimeMs].filter(
      (value) => Number.isFinite(value) && value > 0,
    );
    if (candidates.length === 0) return null;
    return Math.min(...candidates);
  } catch {
    return null;
  }
}

export async function rosterCompanions(
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  try {
    const { token, origin } = await acquireContext(deps);
    const agents = await fetchAgents(
      deps.fetch ?? fetch,
      origin,
      token,
      controller.signal,
    );
    const now = (deps.now ?? Date.now)();
    const installed = await (deps.installEpoch ?? readInstallEpoch)();
    // Never claim an age we cannot stand behind: an unreadable or
    // future-dated install stamp falls back to this moment.
    const activeSince = new Date(
      installed === null || installed > now ? now : installed,
    ).toISOString();
    const mates: readonly CompanionMate[] = [
      {
        personaKey: "startup-advisor",
        agentName: resolveAgentName(agents, "startup-advisor"),
        displayName: "Stacey",
        portraitKey: "stacey",
        activeSince,
      },
      {
        personaKey: "azure-advisor",
        agentName: resolveAgentName(agents, "azure-advisor"),
        displayName: "Azzy",
        portraitKey: "azzy",
        activeSince,
      },
    ];
    const event = parseEvent({ type: "roster", origin, mates });
    sink(event);
    return event;
  } catch (error) {
    return emitFailure(sink, classifyUnknownPredispatch(error));
  } finally {
    clearTimeout(timer);
  }
}

async function drainAcceptedSse(response: Response): Promise<void> {
  const text = await readBoundedText(response);
  const frames = text.split(/\r?\n\r?\n/u);
  let sawDone = false;

  for (const frame of frames) {
    if (frame.trim().length === 0) continue;
    const dataLines = frame
      .split(/\r?\n/u)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart());
    if (dataLines.length === 0) continue;
    const data = dataLines.join("\n");
    if (sawDone) throw new Error("data after terminal stream marker");
    if (data === "[DONE]") {
      sawDone = true;
      continue;
    }
    const parsed = JSON.parse(data) as unknown;
    if (
      !isRecord(parsed) ||
      typeof parsed.type !== "string" ||
      parsed.type === "error"
    ) {
      throw new Error("invalid stream event");
    }
  }

  if (!sawDone) throw new Error("stream ended without terminal marker");
}

export async function sendCompanionMessage(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  const fetchImpl = deps.fetch ?? fetch;
  let route: string | undefined;
  let dispatched = false;
  let accepted = false;

  try {
    const { token, origin } = await acquireContext(deps);
    const headers = authenticatedHeaders(token);
    const agents = await fetchAgents(
      fetchImpl,
      origin,
      token,
      controller.signal,
    );
    const agentName = resolveAgentName(agents, request.personaKey);

    let conversationResponse: Response;
    try {
      conversationResponse = await fetchImpl(`${origin}/api/conversations`, {
        method: "POST",
        headers: { ...headers, "content-type": "application/json" },
        body: JSON.stringify({ agentName }),
        signal: controller.signal,
      });
    } catch {
      throw new PredispatchFailure("not-connected");
    }
    const conversationId = decodeConversationId(
      await readJsonEnvelope(conversationResponse),
    );

    let messagesResponse: Response;
    try {
      messagesResponse = await fetchImpl(
        `${origin}/api/conversations/${encodeURIComponent(conversationId)}/messages`,
        { headers, signal: controller.signal },
      );
    } catch {
      throw new PredispatchFailure("not-connected");
    }
    const desktopAfter = decodeLastMessageId(
      await readJsonEnvelope(messagesResponse),
    );
    route = mateRoute(request.personaKey, desktopAfter);

    const dispatching = parseEvent({
      type: "dispatching",
      requestId: request.requestId,
      personaKey: request.personaKey,
      route,
    });
    sink(dispatching);
    dispatched = true;

    let chatResponse: Response;
    try {
      chatResponse = await fetchImpl(`${origin}/api/chat`, {
        method: "POST",
        headers: { ...headers, "content-type": "application/json" },
        body: JSON.stringify({
          messages: [{ role: "user", content: request.text }],
          agentName,
          conversationId,
        }),
        signal: controller.signal,
      });
    } catch {
      throw new Error("chat dispatch outcome unavailable");
    }

    if (
      !chatResponse.ok ||
      !chatResponse.body ||
      !(chatResponse.headers.get("content-type") ?? "")
        .toLowerCase()
        .includes("text/event-stream")
    ) {
      throw new Error("chat response not accepted");
    }

    const acceptedEvent = parseEvent({
      type: "accepted",
      requestId: request.requestId,
      personaKey: request.personaKey,
      route,
    });
    sink(acceptedEvent);
    accepted = true;

    await drainAcceptedSse(chatResponse);
    const completed = parseEvent({
      type: "completed",
      requestId: request.requestId,
    });
    sink(completed);
    return completed;
  } catch (error) {
    if (!dispatched) {
      return emitFailure(
        sink,
        classifyUnknownPredispatch(error),
        request.requestId,
      );
    }
    const indeterminate = parseEvent({
      type: "indeterminate",
      requestId: request.requestId,
      stage: accepted ? "after-accept" : "dispatch",
      route: route ?? mateRoute(request.personaKey),
    });
    sink(indeterminate);
    return indeterminate;
  } finally {
    clearTimeout(timer);
  }
}
